import React, { useState, useCallback } from "react";
import EmployeeSidebar from "./components/EmployeeSidebar";
import EmployeeEditor from "./components/EmployeeEditor";
import { useEmployees } from "./hooks/useEmployees";
import { employeeDefaults } from "../../../models/employee";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";

/**
 * Employees Page (Colaboradores / Funcionários)
 * Layout Master-Detail: Listagem lateral e editor principal.
 */
const EmployeesPage = () => {
    const { employees, roles, loading, isSaving, actions } = useEmployees();
    const [selectedEmployee, setSelectedEmployee] = useState(employeeDefaults);
    const [isDirty, setIsDirty] = useState(false);

    const handleSelectEmployee = useCallback(async (employee) => {
        if (selectedEmployee?.id === employee.id) return;

        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações pendentes neste colaborador. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedEmployee(employee);
        setIsDirty(false);
    }, [selectedEmployee, isDirty]);

    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar um novo colaborador?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedEmployee(employeeDefaults);
        setIsDirty(false);
    }, [isDirty]);

    const handleSave = useCallback(async (data) => {
        const savedEmployee = await actions.saveEmployee(data);
        if (savedEmployee) {
            setIsDirty(false);
            
            // Se foi uma criação, atualiza o form com o objeto retornado pela Service (que agora contém o ID)
            // Assim não tentará criar de novo caso a pessoa continue editando.
            if (!data.id && savedEmployee.id) {
                setSelectedEmployee(savedEmployee);
            }
        }
    }, [actions]);

    const handleDirtyChange = useCallback((dirty) => setIsDirty(dirty), []);

    // Removido o PageLoader fullscreen. O MasterSidebar lidará com o loading (spinner) e o Editor mostrará o placeholder de "Selecione".

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Colaboradores" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="employees-container mt-3">
                <MasterDetailLayout
                master={
                    <EmployeeSidebar
                        employees={employees || []}
                        roles={roles || []}
                        activeId={selectedEmployee?.id}
                        onSelect={handleSelectEmployee}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                    />
                }
                detail={
                    <EmployeeEditor
                        employee={selectedEmployee}
                        roles={roles || []}
                        onSave={handleSave}
                        isSaving={isSaving}
                        onChange={handleDirtyChange}
                    />
                }
            />
            </div>
        </React.Fragment>
    );
};

export default EmployeesPage;
